import React from 'react';

import { MenuReorderWidget } from 'features/menu/components/menu-reorder-widget';
import MenuTileGrid from 'features/menu/components/menu-tile-grid/menu-tile-grid-badges-experiment';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import withMenuEmptyState from 'features/menu/components/with-menu-empty-state';
import { useAcceleratorBadgesOnMenu } from 'features/menu/hooks/use-accelerator-badges-on-menu';
import { useFetchMainMenu, useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { MenuEmptyState } from 'features/menu/pages/main-menu-page/menu-empty-state';
import { excludeDisabledMenuSections } from 'features/menu/pages/main-menu-page/utils';
import { useStoreContext } from 'features/store';

import { IMainMenuViewOption, MenuObject } from '@rbi-ctg/menu';
import { Helmet } from 'components/helmet';
import { usePullToReload } from 'components/pull-to-reload/pull-to-reload';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeContext } from 'state/service-mode';
import { waitForDuration } from 'utils/wait-for-duration';

const MainMenuComponent = () => {
  useRumPageView('main-menu', 'Main Menu');
  const { data, getPricingAndAvailability, isMenuLoading, hasError } = useMainMenuContext();
  const fetchMainMenu = useFetchMainMenu();
  const { store } = useStoreContext();

  const { serviceMode } = useServiceModeContext();
  const {
    activeDayParts,
    allPricingDependenciesAreSettled,
    forceRefreshActiveDayParts,
    showBurgersForBreakfast,
  } = useMenuContext();

  const menuData = excludeDisabledMenuSections({
    menuData: data?.Menu?.options as IMainMenuViewOption[] | undefined,
    activeDayParts,
    showBurgersForBreakfast,
    serviceMode,
    getPricingAndAvailability,
  });

  const badges = useAcceleratorBadgesOnMenu({
    menuData: menuData as MenuObject[],
    skipExposureLogging: false,
    getPricingAndAvailability,
  });

  const onRefresh = async (): Promise<void> => {
    // This is just an animation feedback of one second
    await waitForDuration(1000);
    forceRefreshActiveDayParts();
    fetchMainMenu(true);
  };

  useMenuScroll();

  const { isPosDataLoading } = useStoreContext();
  const isFetchMainMenuPending = useFetchMainMenuOnMount();
  const loading = isFetchMainMenuPending || isMenuLoading || isPosDataLoading;

  const hasMenuData = !!menuData?.length && allPricingDependenciesAreSettled;

  const pullToReloadProps = usePullToReload({
    onRefresh,
    isDisabled: loading,
  });

  const menuContent =
    hasMenuData && !hasError ? (
      <MenuTileGrid data={menuData} {...pullToReloadProps} badges={badges} />
    ) : (
      <MenuEmptyState
        onRefresh={onRefresh}
        logWhenTrue={!loading}
        logContext={{
          activeDayParts: activeDayParts.map(dayPart => dayPart.key),
          showBurgersForBreakfast,
          serviceMode,
          isFetchedMenuEmpty: !hasMenuData,
          isPricingAndDependenciesSettled: allPricingDependenciesAreSettled,
          storeId: store.number,
          storePosVendor: store.pos?.vendor,
        }}
      />
    );

  return (
    <>
      <Helmet title={t('mainMenu')} />
      {loading ? (
        <MenuSkeletonLoader />
      ) : (
        <>
          <MenuReorderWidget />
          {menuContent}
        </>
      )}
    </>
  );
};

// MainMenu.whyDidYouRender = true;
export const MainMenuPage = withMenuEmptyState(MainMenuComponent);
